import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import vTicketService from '../services/vTicketsService';
import reservationService from '../services/reservationsService';
import subscriptionService from '../services/subscriptionServicies';
import subscriptionUserService from '../services/subscriptionUserService';
import authService from '../services/loginService';
import '../styles/inicioStyle.css';

const Inicio = () => {
  const navigate = useNavigate();
  const [activeTicket, setActiveTicket] = useState(null);
  const [nextReservation, setNextReservation] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentUser = authService.getCurrentUser();
  const userIdCard = currentUser?.idCard;

  useEffect(() => {
    if (userIdCard) {
      fetchDashboardData();
    } else {
      console.error('No se encontró idCard del usuario');
      setLoading(false);
    }
  }, [userIdCard]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Iniciando carga de datos del dashboard para usuario:', userIdCard);

      // Obtener datos en paralelo
      const results = await Promise.allSettled([
        fetchActiveTicket(),
        fetchNextReservation(),
        fetchSubscriptions(),
        fetchUserSubscription()
      ]);

      // Log de resultados para debugging
      results.forEach((result, index) => {
        const names = ['Ticket Activo', 'Próxima Reserva', 'Suscripciones', 'Suscripción Usuario'];
        if (result.status === 'rejected') {
          console.error(`Error al cargar ${names[index]}:`, result.reason);
        }
      });

    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      setError('Error al cargar los datos. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTicket = async () => {
    try {
      console.log('Buscando tickets activos para:', userIdCard);
      const tickets = await vTicketService.getVTicketsByIdCard(userIdCard);
      console.log('Tickets obtenidos:', tickets);
      
      if (Array.isArray(tickets) && tickets.length > 0) {
        // Buscar ticket activo - mapear correctamente los campos del backend
        const active = tickets.find(ticket => 
          ticket.status === 'En Proceso' || // Backend usa 'En Proceso' no 'active'
          ticket.status === 'active' || 
          ticket.status === 'pending' || 
          !ticket.totalpaid // Si no ha sido pagado
        );

        if (active) {
          // Mapear campos del backend a los esperados por el frontend
          const mappedTicket = {
            ...active,
            id: active._id,
            licensePlate: active.vehiclesPlate || active.licensePlate || 'No especificada',
            totalAmount: active.totalpaid || active.totalAmount || 0,
            entryTime: active.billDateIn || active.entryTime,
            exitTime: active.billDateOut || active.exitTime,
            totalTime: calculateTimeDifference(active.billDateIn, active.billDateOut),
            isPaid: active.totalpaid > 0 || active.status === 'Realizado',
            status: mapTicketStatus(active.status)
          };
          
          console.log('Ticket activo mapeado:', mappedTicket);
          setActiveTicket(mappedTicket);
        } else {
          setActiveTicket(null);
        }
      } else {
        setActiveTicket(null);
      }
    } catch (error) {
      console.error('Error al obtener ticket activo:', error);
      setActiveTicket(null);
    }
  };

  const fetchNextReservation = async () => {
    try {
      console.log('Buscando próxima reserva para:', userIdCard);
      const reservation = await reservationService.getNextReservation(userIdCard);
      console.log('Reserva obtenida:', reservation);
      
      if (reservation) {
        // Mapear campos del backend
        const mappedReservation = {
          ...reservation,
          id: reservation._id,
          date: reservation.dateIn || reservation.date,
          startTime: reservation.timeIn || reservation.startTime,
          endTime: reservation.timeOut || reservation.endTime,
          licensePlate: reservation.plate || reservation.licensePlate || 'No especificada',
          parkingName: reservation.idParking?.parkingName || 'Parqueo principal',
          duration: calculateDuration(reservation.timeIn, reservation.timeOut),
          status: mapReservationStatus(reservation.status)
        };
        
        console.log('Reserva mapeada:', mappedReservation);
        setNextReservation(mappedReservation);
      } else {
        setNextReservation(null);
      }
    } catch (error) {
      console.error('Error al obtener próxima reserva:', error);
      setNextReservation(null);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      console.log('Obteniendo todas las suscripciones disponibles');
      const allSubscriptions = await subscriptionService.getAllSubscriptions();
      console.log('Suscripciones obtenidas:', allSubscriptions);
      
      if (Array.isArray(allSubscriptions)) {
        // Mapear y enriquecer las suscripciones con datos adicionales si no existen
        const enrichedSubscriptions = allSubscriptions.map(sub => ({
          ...sub,
          id: sub._id || sub.id,
          name: sub.name || 'Plan Sin Nombre',
          price: sub.rate || sub.price || 0,
          duration: sub.durationPeriod || sub.duration || 'mes',
          featured: sub.featured || false,
          features: sub.features || getDefaultFeatures(sub.name),
          savings: calculateSavings(sub.rate || sub.price)
        }));
        
        setSubscriptions(enrichedSubscriptions);
      } else {
        setSubscriptions([]);
      }
    } catch (error) {
      console.error('Error al obtener suscripciones:', error);
      setSubscriptions([]);
    }
  };

  const fetchUserSubscription = async () => {
    try {
      console.log('Buscando suscripción activa del usuario:', userIdCard);
      const userSub = await subscriptionUserService.getActiveSubscription(userIdCard);
      console.log('Suscripción del usuario:', userSub);
      
      if (userSub && userSub.expiration) {
        // Mapear campos
        const mappedUserSub = {
          ...userSub,
          subscriptionName: userSub.subscriptionId?.name || userSub.subscriptionName || 'Suscripción Activa',
          expiration: userSub.expiration,
          isActive: new Date(userSub.expiration) > new Date()
        };
        
        setUserSubscription(mappedUserSub);
      } else {
        setUserSubscription(null);
      }
    } catch (error) {
      console.error('Error al obtener suscripción del usuario:', error);
      setUserSubscription(null);
    }
  };

  // Funciones auxiliares para mapeo y cálculos
  const mapTicketStatus = (status) => {
    const statusMap = {
      'En Proceso': 'active',
      'Realizado': 'completed',
      'pending': 'pending'
    };
    return statusMap[status] || status;
  };

  const mapReservationStatus = (status) => {
    const statusMap = {
      'Pendiente': 'pending',
      'Realizado': 'confirmed'
    };
    return statusMap[status] || status;
  };

  const calculateTimeDifference = (entryTime, exitTime) => {
    if (!entryTime) return 'En curso...';
    
    const entry = new Date(entryTime);
    const exit = exitTime ? new Date(exitTime) : new Date();
    
    const diff = Math.abs(exit - entry);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} minutos`;
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '2h';
    
    // Parsear las horas si vienen en formato string "HH:MM"
    const parseTime = (timeStr) => {
      if (typeof timeStr === 'string' && timeStr.includes(':')) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      }
      return 0;
    };
    
    const startMinutes = parseTime(startTime);
    const endMinutes = parseTime(endTime);
    const duration = endMinutes - startMinutes;
    
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}min`;
  };

  const calculateSavings = (price) => {
    // Calcular ahorro estimado basado en uso promedio
    const averageMonthlyParking = price * 0.3; // 30% de ahorro estimado
    return Math.round(averageMonthlyParking);
  };

  const getDefaultFeatures = (planName) => {
    // Features por defecto si no vienen del backend
    const defaultFeatures = {
      'Básico': [
        'Acceso al parqueo',
        '10% descuento en reservas',
        'Soporte básico',
        '4 horas diarias máximo'
      ],
      'Premium': [
        'Acceso prioritario al parqueo',
        '20% descuento en reservas',
        'Soporte prioritario',
        'Sin límite de tiempo diario'
      ],
      'VIP': [
        'Acceso VIP al parqueo',
        '30% descuento en reservas',
        'Soporte 24/7',
        'Espacio reservado garantizado'
      ]
    };
    
    return defaultFeatures[planName] || [
      'Acceso al parqueo',
      'Descuentos en reservas',
      'Soporte especializado',
      'Beneficios exclusivos'
    ];
  };

  const handlePayTicket = () => {
    if (activeTicket) {
      navigate('/user-dashboard/payment', { 
        state: { 
          ticketData: activeTicket,
          paymentType: 'ticket' 
        } 
      });
    }
  };

  const handleViewReservationDetails = () => {
    if (nextReservation) {
      navigate('/user-dashboard/reservas', { 
        state: { 
          selectedReservation: nextReservation 
        } 
      });
    }
  };

  const handleSubscribeToPlan = async (subscription) => {
    try {
      const subscriptionData = {
        idCard: userIdCard,
        subscriptionId: subscription.id,
        startDate: new Date().toISOString(),
        paymentMethod: 'pending'
      };

      await subscriptionUserService.createSubscriptionUser(subscriptionData);
      
      // Redirigir al pago
      navigate('/user-dashboard/payment', { 
        state: { 
          subscriptionData: subscription,
          paymentType: 'subscription' 
        } 
      });
    } catch (error) {
      console.error('Error al suscribirse:', error);
      setError('Error al procesar la suscripción. Intenta nuevamente.');
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = Number(amount) || 0;
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha no disponible';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Si es un string con formato HH:MM, devolverlo tal cual
    if (typeof timeString === 'string' && timeString.includes(':')) {
      return timeString;
    }
    
    // Si es una fecha completa, extraer la hora
    try {
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      console.error('Error al formatear hora:', error);
    }
    
    return timeString;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        
        {/* Mensaje de bienvenida */}
        <div className="welcome-section">
          <h1 className="welcome-title">
            ¡Bienvenido, {currentUser?.name || 'Usuario'}!
          </h1>
          <p className="welcome-subtitle">
            Gestiona tus tickets, reservas y suscripciones desde aquí
          </p>
        </div>

        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button 
              className="retry-button" 
              onClick={fetchDashboardData}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Grid principal para las dos tarjetas superiores */}
        <div className="dashboard-grid">
          
          {/* Tickets Activos */}
          <div className="content-section">
            <div className="card active-tickets-card">
              <div className="card-header">
                <span className="card-icon">🎫</span>
                <h2 className="card-title">Tickets Activos</h2>
              </div>
              
              {activeTicket ? (
                <div className="ticket-info">
                  <div className="ticket-detail">
                    <span className="detail-label">Total por pagar:</span>
                    <span className="detail-value price">
                      {formatCurrency(activeTicket.totalAmount)}
                    </span>
                  </div>
                  <div className="ticket-detail">
                    <span className="detail-label">Tiempo transcurrido:</span>
                    <span className="detail-value">
                      {activeTicket.totalTime}
                    </span>
                  </div>
                  <div className="ticket-detail">
                    <span className="detail-label">Hora de ingreso:</span>
                    <span className="detail-value">
                      {formatTime(activeTicket.entryTime)}
                    </span>
                  </div>
                  <div className="ticket-detail">
                    <span className="detail-label">Placa:</span>
                    <span className="detail-value">
                      {activeTicket.licensePlate}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="no-data-message">
                  <span className="no-data-icon">📋</span>
                  <p>No tienes tickets activos en este momento</p>
                  <small>Los tickets aparecerán aquí cuando ingreses al parqueo</small>
                </div>
              )}
              
              <div className="card-footer">
                {activeTicket ? (
                  <button 
                    className="action-button pay-button"
                    onClick={handlePayTicket}
                  >
                    💳 Pagar ahora
                  </button>
                ) : (
                  <button 
                    className="action-button secondary-button"
                    onClick={() => navigate('/user-dashboard/tiquetes')}
                  >
                    📚 Ver historial
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Próximas Reservaciones */}
          <div className="content-section">
            <div className="card reservation-card">
              <div className="card-header">
                <span className="card-icon">📅</span>
                <h2 className="card-title">Próxima Reservación</h2>
              </div>
              
              {nextReservation ? (
                <div className="reservation-content">
                  <div className="reservation-header">
                    <div className="reservation-date">
                      <div className="reservation-month">
                        {new Date(nextReservation.date).toLocaleDateString('es-ES', { month: 'short' }).toUpperCase()}
                      </div>
                      <div className="reservation-day">
                        {new Date(nextReservation.date).getDate()}
                      </div>
                    </div>
                    <div className="reservation-info">
                      <div className="reservation-time">
                        <strong>Hora:</strong> {formatTime(nextReservation.startTime)} - {formatTime(nextReservation.endTime)}
                      </div>
                      <div className="reservation-plate">
                        <strong>Placa:</strong> {nextReservation.licensePlate}
                      </div>
                      <div className="reservation-parking">
                        📍 {nextReservation.parkingName}
                      </div>
                    </div>
                  </div>
                  
                  <div className="reservation-details">
                    <div className="detail-item">
                      <div className="detail-label">Fecha completa</div>
                      <div className="detail-value">
                        {formatDate(nextReservation.date)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">Duración</div>
                      <div className="detail-value">
                        {nextReservation.duration}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">Estado</div>
                      <div className={`detail-value status-${nextReservation.status}`}>
                        {nextReservation.status === 'confirmed' ? '✓ Confirmada' : 
                         nextReservation.status === 'pending' ? '⏳ Pendiente' : 
                         nextReservation.status}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-data-message">
                  <span className="no-data-icon">📅</span>
                  <p>No tienes reservas próximas</p>
                  <small>Haz tu primera reserva para verla aquí</small>
                </div>
              )}
              
              <div className="card-footer">
                {nextReservation ? (
                  <button 
                    className="action-button view-button"
                    onClick={handleViewReservationDetails}
                  >
                    👁️ Ver detalles
                  </button>
                ) : (
                  <button 
                    className="action-button primary-button"
                    onClick={() => navigate('/user-dashboard/reservas')}
                  >
                    ➕ Hacer reserva
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Suscripción Actual */}
        {userSubscription && userSubscription.isActive && (
          <div className="current-subscription-section">
            <div className="card subscription-status-card">
              <div className="card-header">
                <span className="card-icon">⭐</span>
                <h2 className="card-title">Tu Suscripción Activa</h2>
              </div>
              <div className="subscription-status-content">
                <div className="subscription-info">
                  <h3 className="subscription-name">{userSubscription.subscriptionName}</h3>
                  <p className="subscription-expires">
                    Expira el: {formatDate(userSubscription.expiration)}
                  </p>
                </div>
                <div className="subscription-actions">
                  <button 
                    className="action-button secondary-button"
                    onClick={() => navigate('/user-dashboard/personal')}
                  >
                    🔧 Gestionar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sección de Planes - Solo si no tiene suscripción activa */}
        {(!userSubscription || !userSubscription.isActive) && (
          <div className="plans-section">
            <div className="card plans-card">
              <div className="section-header">
                <h2 className="section-title">Elige tu plan de suscripción</h2>
                <p className="section-subtitle">
                  Selecciona el plan que mejor se adapte a tus necesidades y ahorra en tus parqueos
                </p>
              </div>
              
              <div className="plans-grid">
                {subscriptions.length > 0 ? (
                  subscriptions.map((subscription, index) => (
                    <div 
                      key={subscription.id} 
                      className={`plan-card ${subscription.featured || index === 1 ? 'featured' : ''}`}
                    >
                      {(subscription.featured || index === 1) && (
                        <div className="plan-badge">⭐ Recomendado</div>
                      )}
                      
                      <div className="plan-header">
                        <div className="plan-name">{subscription.name}</div>
                        <div className="plan-price">
                          {formatCurrency(subscription.price)}
                          <span className="per-period">
                            /{subscription.duration}
                          </span>
                        </div>
                        {subscription.savings > 0 && (
                          <div className="plan-savings">
                            💰 Ahorras {formatCurrency(subscription.savings)}
                          </div>
                        )}
                      </div>
                      
                      <ul className="plan-features">
                        {subscription.feature.map((feature, featureIndex) => (
                          <li key={featureIndex}>
                            <span className="feature-check">✓</span> {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <button 
                        className={`plan-button ${subscription.featured || index === 1 ? 'primary' : ''}`}
                        onClick={() => handleSubscribeToPlan(subscription)}
                      >
                        ✨ Seleccionar Plan
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="no-plans-message">
                    <p>No hay planes disponibles en este momento</p>
                    <small>Vuelve más tarde para ver nuestras ofertas</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inicio;